/**
 * LocationFields.jsx
 * Country → State / Province → Postcode
 * FIX #3: Flag emoji shown in dropdown list rows, plain name shown in input box
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { useCountries, useStates, getPostcodeLabel } from "../../_services/locationData.service";
import { OPTION_SELECTED_TEXT, optionBackground } from "../Shared/dropdownColors";

import { T } from "./ContactShared";

/* ── Tiny spinner ── */
function TinySpinner() {
    return (
        <>
            <style>{`@keyframes lf-spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{
                display: "inline-block", width: 12, height: 12,
                border: `2px solid ${T.border}`, borderTopColor: T.accent,
                borderRadius: "50%", animation: "lf-spin 0.6s linear infinite",
                verticalAlign: "middle",
            }} />
        </>
    );
}

/* ════════════════════════════════════════════
   Country dropdown
   - Input box shows: "🇲🇾  Malaysia"  (flag + space + name)
   - Dropdown rows show: flag (large) + name
   - value stored is the plain country name ("Malaysia")
════════════════════════════════════════════ */
function CountryDropdown({ value, onChange, error, required, loading: externalLoading, options }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const wrapRef = useRef(null);

    /* Find matching option to get flag */
    const selected = useMemo(
        () => options.find((o) => o.value === value),
        [options, value]
    );

    /* What shows in the input when closed — plain country name (img can't go in <input> value) */
    const displayText = selected ? selected.value : value || "";

    /* Filter by name (not flag) */
    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return q
            ? options.filter((o) => o.value.toLowerCase().includes(q))
            : options;
    }, [options, query]);

    /* Close on outside click */
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false); setQuery("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const isDisabled = externalLoading;
    const borderColor = error ? T.danger : focused ? T.accent : T.border;

    const handleInput = (e) => {
        /* User is typing — search by name only (strip any leading emoji) */
        const raw = e.target.value.replace(/^\p{Emoji_Presentation}\s*/u, "");
        setQuery(raw);
        setOpen(true);
    };

    const handleSelect = (opt) => {
        onChange({ target: { name: "country", value: opt.value } });
        setOpen(false); setQuery("");
    };

    return (
        <div style={{ marginBottom: 14, position: "relative" }} ref={wrapRef}>
            <label style={{
                display: "block", fontSize: 11,
                fontWeight: 600, color: T.textMuted, marginBottom: 5,
            }}>
                Country{required && <span style={{ color: T.danger }}> *</span>}
            </label>

            <div style={{ position: "relative" }}>
                <input
                    type="text" autoComplete="off"
                    /* Show flag+name when closed, plain typed query when open */
                    value={open ? query : displayText}
                    placeholder={externalLoading ? "Loading countries…" : "Search country…"}
                    disabled={isDisabled}
                    onChange={handleInput}
                    onFocus={() => { setFocused(true); if (!isDisabled) { setOpen(true); setQuery(""); } }}
                    onBlur={() => setFocused(false)}
                    style={{
                        width: "100%", height: 36, padding: "0 32px 0 11px",
                        fontSize: 13,
                        color: isDisabled ? T.textLight : T.textPrimary,
                        background: isDisabled ? T.bg : "#fff",
                        fontFamily: "inherit",
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: T.radiusSm, outline: "none",
                        boxSizing: "border-box", display: "block",
                        boxShadow: focused && !error
                            ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                        cursor: isDisabled ? "not-allowed" : "text",
                    }}
                />

                <span
                    onClick={() => { if (!isDisabled) { setOpen((o) => !o); setQuery(""); } }}
                    style={{
                        position: "absolute", right: 9, top: "50%",
                        transform: "translateY(-50%)",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center",
                    }}
                >
                    {externalLoading
                        ? <TinySpinner />
                        : <FiChevronDown
                            size={14}
                            style={{
                                transition: "transform 0.22s ease, color 0.15s",
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                color: open ? T.accent : T.textLight,
                            }}
                        />
                    }
                </span>
            </div>

            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}

            {/* Dropdown list — shows flag + full name */}
            {open && !externalLoading && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    background: "#fff", border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
                    zIndex: 3000, maxHeight: 260, overflowY: "auto",
                    animation: "lf-dropdown-in 0.15s ease",
                }}>
                    <style>{`
                        @keyframes lf-dropdown-in {
                            from { opacity: 0; transform: translateY(-6px); }
                            to   { opacity: 1; transform: translateY(0); }
                        }
                    `}</style>

                    {filtered.length === 0 ? (
                        <div style={{
                            padding: "10px 12px", fontSize: 12,
                            color: T.textLight, textAlign: "center",
                        }}>
                            No countries found
                        </div>
                    ) : (
                        filtered.map((opt) => (
                            <div
                                key={opt.value}
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                                style={{
                                    padding: "8px 12px", fontSize: 13, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 10,
                                    color: opt.value === value ? OPTION_SELECTED_TEXT : T.textPrimary,
                                    background: optionBackground({ selected: opt.value === value }),
                                    fontWeight: opt.value === value ? 600 : 400,
                                    transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = optionBackground({ active: true, selected: opt.value === value });
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = optionBackground({ selected: opt.value === value });
                                }}
                            >
                                {/* Flag shown as PNG image in list row */}
                                {opt.flag ? (
                                    <img
                                        src={opt.flag}
                                        alt=""
                                        style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
                                    />
                                ) : (
                                    <span style={{ width: 22, flexShrink: 0 }} />
                                )}
                                <span>{opt.value}</span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

/* ════════════════════════════════════════════
   Generic state / plain-text dropdown
════════════════════════════════════════════ */
function SimpleDropdown({
    label, name, value, onChange,
    options = [], placeholder, required, error,
    loading: externalLoading = false, disabled = false,
    allowFreeText = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const wrapRef = useRef(null);


    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false); setQuery("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const isDisabled = disabled || externalLoading;
    const borderColor = error ? T.danger : focused ? T.accent : T.border;

    const handleInput = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        if (allowFreeText) onChange({ target: { name, value: e.target.value } });
    };

    const handleSelect = (opt) => {
        onChange({ target: { name, value: opt } });
        setOpen(false); setQuery("");
    };

    return (
        <div style={{ marginBottom: 14, position: "relative" }} ref={wrapRef}>
            <label style={{
                display: "block", fontSize: 11,
                fontWeight: 600, color: T.textMuted, marginBottom: 5,
            }}>
                {label}{required && <span style={{ color: T.danger }}> *</span>}
            </label>

            <div style={{ position: "relative" }}>
                <input
                    type="text" autoComplete="off"
                    value={open ? query : value || ""}
                    placeholder={externalLoading ? "Loading…" : placeholder}
                    disabled={isDisabled}
                    onChange={handleInput}
                    onFocus={() => { setFocused(true); if (!isDisabled) { setOpen(true); setQuery(""); } }}
                    onBlur={() => setFocused(false)}
                    style={{
                        width: "100%", height: 36, padding: "0 32px 0 11px",
                        fontSize: 13,
                        color: isDisabled ? T.textLight : T.textPrimary,
                        background: isDisabled ? T.bg : "#fff",
                        fontFamily: "inherit",
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: T.radiusSm, outline: "none",
                        boxSizing: "border-box", display: "block",
                        boxShadow: focused && !error
                            ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                        cursor: isDisabled ? "not-allowed" : "text",
                    }}
                />
                <span
                    onClick={() => { if (!isDisabled) { setOpen((o) => !o); setQuery(""); } }}
                    style={{
                        position: "absolute", right: 9, top: "50%",
                        transform: "translateY(-50%)",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center",
                    }}
                >
                    {externalLoading
                        ? <TinySpinner />
                        : <FiChevronDown
                            size={14}
                            style={{
                                transition: "transform 0.22s ease, color 0.15s",
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                color: open ? T.accent : T.textLight,
                            }}
                        />
                    }
                </span>
            </div>

            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}

            {open && !externalLoading && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    background: "#fff", border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
                    zIndex: 3000, maxHeight: 240, overflowY: "auto",
                    animation: "lf-dropdown-in 0.15s ease",
                }}>
                    {filtered.length === 0 ? (
                        <div style={{
                            padding: "10px 12px", fontSize: 12,
                            color: T.textLight, textAlign: "center",
                        }}>
                            {query ? "No results found" : "No options available"}
                        </div>
                    ) : (
                        filtered.map((opt) => (
                            <div
                                key={opt}
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                                style={{
                                    padding: "9px 12px", fontSize: 13, cursor: "pointer",
                                    color: opt === value ? OPTION_SELECTED_TEXT : T.textPrimary,
                                    background: optionBackground({ selected: opt === value }),
                                    fontWeight: opt === value ? 600 : 400,
                                    transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = optionBackground({ active: true, selected: opt === value });
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = optionBackground({ selected: opt === value });
                                }}
                            >
                                {opt}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Plain text input ── */
function TextInput({ label, name, value, onChange, placeholder, required, error }) {
    const [focused, setFocused] = useState(false);
    const borderColor = error ? T.danger : focused ? T.accent : T.border;
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{
                display: "block", fontSize: 11,
                fontWeight: 600, color: T.textMuted, marginBottom: 5,
            }}>
                {label}{required && <span style={{ color: T.danger }}> *</span>}
            </label>
            <input
                type="text" name={name} value={value ?? ""} onChange={onChange}
                placeholder={placeholder}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                style={{
                    width: "100%", height: 36, padding: "0 11px",
                    fontSize: 13, color: T.textPrimary,
                    background: "#fff", fontFamily: "inherit",
                    border: `1.5px solid ${borderColor}`,
                    borderRadius: T.radiusSm, outline: "none",
                    boxSizing: "border-box", display: "block",
                    boxShadow: focused && !error
                        ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                }}
            />
            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* ════════════════════════════════════════════
   Main export
════════════════════════════════════════════ */
export default function LocationFields({ draft, onChange, errors = {}, required = false }) {
    const { countries, loading: loadingCountries } = useCountries();
    const { states, loading: loadingStates } = useStates(draft.country);

    /* Build option list — value = plain name, flag kept separately */
    const countryOptions = useMemo(() =>
        countries.map((c) => ({ value: c.name, flag: c.flag, code: c.code })),
        [countries]);

    /* Postcode label adapts by country code */
    const countryCode = useMemo(() => {
        const match = countries.find((c) => c.name === draft.country);
        return match?.code || "";
    }, [countries, draft.country]);

    const postcodeLabel = getPostcodeLabel(countryCode);

    /* Country change → clear state + postcode */
    const handleCountryChange = (e) => {
        onChange(e);
        onChange({ target: { name: "state", value: "" } });
        onChange({ target: { name: "postcode", value: "" } });
    };

    return (
        <>
            {/* FIX #3: CountryDropdown shows flag emoji in list, plain name in input */}
            <CountryDropdown
                value={draft.country}
                onChange={handleCountryChange}
                options={countryOptions}
                error={errors.country}
                required={required}
                loading={loadingCountries}
            />

            {/* State / Province */}
            <SimpleDropdown
                label="State / Province"
                name="state"
                value={draft.state}
                onChange={onChange}
                options={states}
                placeholder={draft.country ? "Select or type state…" : "Select country first"}
                required={required}
                error={errors.state}
                loading={loadingStates}
                disabled={!draft.country}
                allowFreeText
            />

            {/* Postcode — FIX #2: NOT required (no *) */}
            <TextInput
                label={postcodeLabel}
                name="postcode"
                value={draft.postcode}
                onChange={onChange}
                placeholder="e.g. 40150 (optional)"
                error={errors.postcode}
            /* required intentionally omitted */
            />
        </>
    );
}